import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDateShort, formatDateTimeParts, formatTime } from "@/lib/utils";

function startOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0); return x;
}
function fmt(d) { return d.toISOString().slice(0, 10); }
function isSameDay(a, b) { return fmt(a) === fmt(b); }

export default function DoctorCalendar({ apts, schedule, onRefresh, doctorId }) {
  const { t } = useApp();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ patient_name: "", patient_phone: "", date: fmt(new Date()), time: "09:00", notes: "" });

  const days = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
    }), [weekStart]);

  // Build hour rows from earliest start to latest end across enabled days
  const { hourStart, hourEnd } = useMemo(() => {
    let hs = 8, he = 18;
    (schedule || []).forEach(s => {
      if (!s.enabled) return;
      const [sh] = s.start.split(":").map(Number);
      const [eh, em] = s.end.split(":").map(Number);
      hs = Math.min(hs, sh);
      he = Math.max(he, eh + (em > 0 ? 1 : 0));
    });
    return { hourStart: hs, hourEnd: he };
  }, [schedule]);

  const hours = useMemo(() => Array.from({ length: hourEnd - hourStart }, (_, i) => `${String(hourStart + i).padStart(2, "0")}:00`), [hourStart, hourEnd]);

  const cellFor = (day, hour) => {
    const dayIdx = (day.getDay() + 6) % 7;
    const cfg = (schedule || []).find(s => s.day === dayIdx && s.enabled);
    if (!cfg) return { type: "off" };
    const [ch] = hour.split(":").map(Number);
    const [sh, sm] = cfg.start.split(":").map(Number);
    const [eh, em] = cfg.end.split(":").map(Number);
    const inRange = (ch * 60) >= (sh * 60 + sm) && (ch * 60) < (eh * 60 + em);
    if (!inRange) return { type: "off" };
    for (const b of cfg.breaks || []) {
      const [bsh, bsm] = b.start.split(":").map(Number);
      const [beh, bem] = b.end.split(":").map(Number);
      if ((ch * 60) >= (bsh * 60 + bsm) && (ch * 60) < (beh * 60 + bem)) return { type: "break" };
    }
    const apt = apts.find(a => a.date === fmt(day) && a.time.startsWith(hour.slice(0, 2)) && a.status !== "cancelled");
    if (apt) return { type: apt.status === "pending_verification" ? "pending" : "confirmed", apt };
    return { type: "free" };
  };

  const addManual = async () => {
    try {
      const [d, mo, y] = manual.date.includes("/") ? manual.date.split("/") : manual.date.split("-").reverse();
      const iso = `${y}-${String(mo).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      await api.post("/appointments", {
        doctor_id: doctorId, date: iso, time: manual.time,
        patient_name: manual.patient_name || "Walk-in",
        patient_email: `walkin-${Date.now()}@agendik.local`,
        patient_phone: manual.patient_phone || "manual",
        notes: manual.notes || "Cita añadida manualmente",
      });
      toast.success("Cita añadida");
      setManualOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cancel = async (id) => {
    await api.post(`/doctor/appointments/${id}/cancel`);
    toast.success("Cancelada"); onRefresh();
  };

  const weekAppointments = apts.filter(a => a.status !== "cancelled" &&
    days.some(d => fmt(d) === a.date)).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  return (
    <>
      <Card><CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button data-testid="cal-prev" size="icon" variant="outline"
                    onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
                    className="h-8 w-8"><ChevronLeft size={14}/></Button>
            <div className="text-sm font-semibold text-slate-800">
              {formatDateShort(days[0])} — {formatDateShort(days[6])}
            </div>
            <Button data-testid="cal-next" size="icon" variant="outline"
                    onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
                    className="h-8 w-8"><ChevronRight size={14}/></Button>
          </div>
          <Button data-testid="add-manual-btn" size="sm" onClick={() => setManualOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 h-8">
            <Plus size={14} className="mr-1"/> {t.doctorDash.addManual}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-xs">
            <thead>
              <tr>
                <th className="p-2 text-slate-500 w-14"></th>
                {days.map((d, i) => (
                  <th key={i} className={`p-2 font-semibold text-center ${isSameDay(d, new Date()) ? "bg-blue-50 text-blue-700 rounded-t-lg" : "text-slate-600"}`}>
                    <div>{t.days[i]}</div>
                    <div className="text-lg">{d.getDate()}</div>
                    {isSameDay(d, new Date()) && <div className="text-[10px] uppercase tracking-wider">{t.doctorDash.today}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map(h => (
                <tr key={h} className="border-t border-slate-100">
                  <td className="p-1 text-slate-400 font-medium">{h}</td>
                  {days.map((d, di) => {
                    const c = cellFor(d, h);
                    if (c.type === "off") return <td key={di} className="p-0.5"><div className="h-10 rounded bg-slate-50"/></td>;
                    if (c.type === "break") return <td key={di} className="p-0.5"><div className="h-10 rounded bg-slate-200 text-slate-500 flex items-center justify-center text-[10px] font-medium">{t.doctorDash.pause}</div></td>;
                    if (c.type === "confirmed") return <td key={di} className="p-0.5">
                      <div data-testid={`cal-apt-${c.apt.id}`} className="h-10 rounded bg-blue-600 text-white px-1.5 py-1 text-[10px] font-semibold truncate">
                        {c.apt.patient_name} · {formatTime(c.apt.time)}
                      </div>
                    </td>;
                    if (c.type === "pending") return <td key={di} className="p-0.5">
                      <div className="h-10 rounded bg-amber-400 text-white px-1.5 py-1 text-[10px] font-semibold truncate">
                        {c.apt.patient_name} · {t.doctorDash.pending}
                      </div>
                    </td>;
                    return <td key={di} className="p-0.5"><div className="h-10 rounded bg-emerald-50 border border-emerald-100"/></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 text-[11px]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-600"/> {t.doctorDash.confirmed}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400"/> {t.doctorDash.pending}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-200"/> {t.doctorDash.pause}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-100"/> Libre</span>
        </div>
      </CardContent></Card>

      <Card className="mt-5"><CardContent className="p-5">
        <h3 className="font-semibold text-slate-900 mb-3">{t.doctorDash.upcoming}</h3>
        {weekAppointments.length === 0 ? <p className="text-sm text-slate-500">–</p> :
          <div className="space-y-2">
            {weekAppointments.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200">
                <div>
                  <div className="font-medium text-slate-900">{a.patient_name}</div>
                  <div className="text-xs text-slate-500">{formatDateTimeParts(a.date, a.time)}</div>
                </div>
                <Button data-testid={`cal-cancel-${a.id}`} size="sm" variant="outline"
                        onClick={() => cancel(a.id)}
                        className="border-red-200 text-red-600 hover:bg-red-50 h-8">
                  <X size={14} className="mr-1"/> {t.doctorDash.cancel}
                </Button>
              </div>
            ))}
          </div>}
      </CardContent></Card>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t.doctorDash.addManual}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre paciente</Label><Input data-testid="manual-name" value={manual.patient_name} onChange={e => setManual({...manual, patient_name: e.target.value})}/></div>
            <div><Label>Teléfono</Label><Input data-testid="manual-phone" value={manual.patient_phone} onChange={e => setManual({...manual, patient_phone: e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Fecha</Label><Input data-testid="manual-date" type="date" value={manual.date} onChange={e => setManual({...manual, date: e.target.value})}/></div>
              <div><Label>Hora</Label><Input data-testid="manual-time" type="time" value={manual.time} onChange={e => setManual({...manual, time: e.target.value})}/></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="manual-save-btn" onClick={addManual} className="bg-blue-600 hover:bg-blue-700 w-full">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
